/**
 * ListMovimientosBancarios.js — Consulta de movimientos bancarios (solo lectura).
 * Muestra todos los BancoMovimiento del condominio (originados por recibos,
 * traslados, conciliaciones, etc.) con filtros y exportación a Excel.
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { TableToolbar, FilterField, FilterInput, FilterSelect } from "../../shared/TableToolbar";
import invoiceStyles from "../../invoices/styles/invoice.module.css";

function fmtDate(iso) {
  if (!iso || iso.length < 10) return iso ?? "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export default function ListMovimientosBancarios() {
  const { t } = useTranslation();
  const [items,       setItems]       = useState([]);
  const [count,       setCount]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [search,      setSearch]      = useState("");
  const [fechaDesde,  setFechaDesde]  = useState("");
  const [fechaHasta,  setFechaHasta]  = useState("");
  const [cuentaId,    setCuentaId]    = useState("");
  const [cuentas,     setCuentas]     = useState([]);
  const [totales,     setTotales]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [exporting,   setExporting]   = useState(false);

  const PAGE_SIZE  = 20;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const activeFilterCount = [fechaDesde, fechaHasta, cuentaId].filter(Boolean).length;

  const cuentaNombre = (id) => {
    const c = cuentas.find((c) => String(c.id) === String(id));
    return c ? `${c.banco} — ${c.numero_cuenta}` : "—";
  };
  const cuentaSimbolo = (id) => {
    const c = cuentas.find((c) => String(c.id) === String(id));
    return c?.moneda === "USD" ? "$" : c?.moneda === "EUR" ? "€" : "₡";
  };

  function buildParams(pageSize, pageNum) {
    const qs = new URLSearchParams({ page: pageNum, page_size: pageSize });
    if (search.trim()) qs.set("search", search.trim());
    if (fechaDesde)     qs.set("fecha_desde", fechaDesde);
    if (fechaHasta)     qs.set("fecha_hasta", fechaHasta);
    if (cuentaId)       qs.set("cuenta_bancaria", cuentaId);
    return qs;
  }

  const fetchData = useCallback(() => {
    setLoading(true);
    apiFetch(`/api/finance/bancoMovimiento/?${buildParams(PAGE_SIZE, page)}`)
      .then((r) => r.json())
      .then((d) => { setItems(d.results ?? []); setCount(d.count ?? 0); })
      .catch(console.error)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, fechaDesde, fechaHasta, cuentaId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Totales de TODO lo que pasa el filtro, no solo de la página visible: el
  // backend los suma aparte porque la página trae 20 filas de las que puede
  // haber cientos.
  const fetchTotales = useCallback(() => {
    const qs = buildParams(PAGE_SIZE, 1);
    qs.delete("page");
    qs.delete("page_size");
    apiFetch(`/api/finance/bancoMovimiento/totales/?${qs}`)
      .then((r) => r.json())
      .then((d) => setTotales(d.totales ?? []))
      .catch(() => setTotales([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, fechaDesde, fechaHasta, cuentaId]);

  useEffect(() => { fetchTotales(); }, [fetchTotales]);

  useEffect(() => {
    apiFetch("/api/finance/cuentaBancaria/?all=1")
      .then((r) => r.json())
      .then((d) => setCuentas(Array.isArray(d) ? d : d.results ?? []))
      .catch(() => {});
  }, []);

  async function exportarExcel() {
    setExporting(true);
    try {
      const res = await apiFetch(`/api/finance/bancoMovimiento/?${buildParams(10000, 1)}`);
      const d = await res.json();
      const filas = (d.results ?? []).map((m) => ({
        [t("movimientos.colFecha")]:       fmtDate(m.fecha),
        [t("movimientos.colCuenta")]:      cuentaNombre(m.cuenta_bancaria),
        [t("movimientos.colDocumento")]:   m.documento || "",
        [t("movimientos.colDescripcion")]: m.descripcion || "",
        [t("movimientos.colDebito")]:      Number(m.debito) || 0,
        [t("movimientos.colCredito")]:     Number(m.credito) || 0,
        [t("movimientos.colEstado")]: m.conciliacion
          ? t("movimientos.estadoConciliado")
          : t("movimientos.estadoPendiente"),
      }));
      const ws = XLSX.utils.json_to_sheet(filas);
      ws["!cols"] = [{ wch: 12 }, { wch: 28 }, { wch: 16 }, { wch: 36 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Movimientos");
      const fecha = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
      XLSX.writeFile(wb, `Movimientos_Bancarios_${fecha}.xlsx`);
    } catch (e) {
      console.error(e);
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <span className={invoiceStyles.inactive}>
        <Link to="/"><span className={invoiceStyles.inactive}>{t("common.inicio")} /</span></Link>
        <span className={invoiceStyles.active}> {t("movimientos.titulo")}</span>
      </span>

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2" style={{ marginBottom: 6 }}>
        <div>
          <h2 className="title mt-2 mb-2">{t("movimientos.titulo")}</h2>
          <p className="mb-3" style={{ color: "var(--text-muted)" }}>{t("movimientos.subtitulo")}</p>
        </div>
        <button onClick={exportarExcel} disabled={exporting || count === 0}
          style={{
            display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 18px",
            background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.35)",
            borderRadius: 8, color: "#4ade80", fontSize: "0.85rem", fontWeight: 600,
            cursor: exporting || count === 0 ? "not-allowed" : "pointer",
            opacity: exporting || count === 0 ? 0.5 : 1, marginTop: 8,
          }}>
          <i className={`fa-solid ${exporting ? "fa-spinner fa-spin" : "fa-file-excel"}`}/>
          {exporting ? t("movimientos.exportando") : t("movimientos.exportarExcel")}
        </button>
      </div>

      <div className={invoiceStyles["table-container"]}>
        <TableToolbar
          search={search}
          onSearch={(v) => { setSearch(v); setPage(1); }}
          searchPlaceholder={t("movimientos.buscarPlaceholder")}
          loading={loading}
          activeFilterCount={activeFilterCount}
          onClearFilters={() => { setFechaDesde(""); setFechaHasta(""); setCuentaId(""); setPage(1); }}
          filters={
            <>
              <FilterField label={t("movimientos.filtroCuenta")}>
                <FilterSelect value={cuentaId} onChange={(e) => { setCuentaId(e.target.value); setPage(1); }}>
                  <option value="">{t("movimientos.todasLasCuentas")}</option>
                  {cuentas.map((c) => (
                    <option key={c.id} value={c.id}>{c.banco} — {c.numero_cuenta} ({c.moneda})</option>
                  ))}
                </FilterSelect>
              </FilterField>
              <FilterField label={t("common.fechaDesde")}>
                <FilterInput type="date" value={fechaDesde} onChange={(e) => { setFechaDesde(e.target.value); setPage(1); }} />
              </FilterField>
              <FilterField label={t("common.fechaHasta")}>
                <FilterInput type="date" value={fechaHasta} onChange={(e) => { setFechaHasta(e.target.value); setPage(1); }} />
              </FilterField>
            </>
          }
        />

        <div className={`${invoiceStyles["table-body"]} ${invoiceStyles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th>{t("movimientos.colFecha")}</th>
                <th>{t("movimientos.colCuenta")}</th>
                <th>{t("movimientos.colDocumento")}</th>
                <th>{t("movimientos.colDescripcion")}</th>
                <th><div style={{ textAlign: "right" }}>{t("movimientos.colDebito")}</div></th>
                <th><div style={{ textAlign: "right" }}>{t("movimientos.colCredito")}</div></th>
                <th className="text-center">{t("movimientos.colEstado")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 32, color: "var(--text-secondary)" }}>
                  <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 8, color: "#f97316" }}/>
                  {t("movimientos.cargando")}
                </td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>
                  <i className="fa-solid fa-inbox" style={{ marginRight: 8, fontSize: "1.2rem" }}/>
                  {t("movimientos.sinMovimientos")}
                </td></tr>
              ) : items.map((m) => {
                const simbolo = cuentaSimbolo(m.cuenta_bancaria);
                return (
                  <tr key={m.id}>
                    <td>{fmtDate(m.fecha)}</td>
                    <td style={{ color: "var(--text-primary)", fontSize: 13 }}>{cuentaNombre(m.cuenta_bancaria)}</td>
                    <td>{m.documento || "—"}</td>
                    <td style={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      title={m.descripcion}>
                      {m.descripcion || "—"}
                    </td>
                    <td>
                      <div style={{ textAlign: "right", fontFamily: "monospace",
                        color: Number(m.debito) > 0 ? "#f87171" : "var(--text-muted)",
                        fontWeight: Number(m.debito) > 0 ? 600 : 400 }}>
                        {Number(m.debito) > 0 ? `${simbolo}${formatCurrency(m.debito)}` : "—"}
                      </div>
                    </td>
                    <td>
                      <div style={{ textAlign: "right", fontFamily: "monospace",
                        color: Number(m.credito) > 0 ? "#4ade80" : "var(--text-muted)",
                        fontWeight: Number(m.credito) > 0 ? 600 : 400 }}>
                        {Number(m.credito) > 0 ? `${simbolo}${formatCurrency(m.credito)}` : "—"}
                      </div>
                    </td>
                    <td className="text-center">
                      {m.conciliacion ? (
                        <span style={{ padding: "2px 9px", borderRadius: 5, fontSize: "0.72rem", fontWeight: 700,
                          background: "rgba(74,222,128,0.12)", color: "#4ade80" }}>
                          <i className="fa-solid fa-check" style={{ marginRight: 5 }}/>{t("movimientos.estadoConciliado")}
                        </span>
                      ) : (
                        <span style={{ padding: "2px 9px", borderRadius: 5, fontSize: "0.72rem", fontWeight: 700,
                          background: "rgba(251,191,36,0.12)", color: "#fbbf24" }}>
                          {t("movimientos.estadoPendiente")}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className={invoiceStyles["table-footer"]}>
          <p>
            {loading ? (
              <span style={{ color: "#f97316", fontSize: "0.83rem" }}>
                <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 5 }}/>
                {t("common.cargando")}
              </span>
            ) : (
              <>
                <span>{count}</span> {t("movimientos.registros")} · {t("common.pagina")}{" "}
                <span>{page}</span> {t("common.de")} <span>{Math.max(1, totalPages)}</span>
                {totales.map((tot) => {
                  const simbolo = tot.moneda === "USD" ? "$" : tot.moneda === "EUR" ? "€" : "₡";
                  return (
                    <span key={tot.moneda} style={{ marginLeft: 14, fontSize: "0.82rem" }}>
                      <span style={{ color: "var(--text-muted)" }}>
                        {t("movimientos.totalDebitos")}:{" "}
                      </span>
                      <span style={{ fontFamily: "monospace", color: "#f87171", fontWeight: 600 }}>
                        {simbolo}{formatCurrency(tot.debito)}
                      </span>
                      <span style={{ color: "var(--text-muted)", marginLeft: 10 }}>
                        {t("movimientos.totalCreditos")}:{" "}
                      </span>
                      <span style={{ fontFamily: "monospace", color: "#4ade80", fontWeight: 600 }}>
                        {simbolo}{formatCurrency(tot.credito)}
                      </span>
                    </span>
                  );
                })}
              </>
            )}
          </p>
          <div className={`${invoiceStyles["pagination-buttons"]} d-flex gap-2`}>
            <button disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}
              className={`btn btn-sm ${invoiceStyles["btn-prev"]} d-flex gap-2 align-items-center`}>
              {t("common.anterior")}
            </button>
            <button disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}
              className={`btn btn-sm ${invoiceStyles["btn-next"]} d-flex gap-2 align-items-center`}>
              {t("common.siguiente")}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
